import { useRef, useLayoutEffect, useEffect } from 'react';

/**
 * Renderiza HTML inyectado (dangerouslySetInnerHTML) bloqueando
 * selección, copia y corte del texto mediante 3 capas:
 *  1. CSS user-select:none (clase padre + hijos)
 *  2. inline style !important en cada nodo (anula estilos del CMS)
 *  3. listeners en document para selectstart / selectionchange / copy / cut
 */
const noSelectInlineStyle = {
    userSelect: 'none',
    WebkitUserSelect: 'none',
    MozUserSelect: 'none',
    msUserSelect: 'none',
    WebkitTouchCallout: 'none',
};

function applyNoSelectToNode(el) {
    if (!el || el.nodeType !== Node.ELEMENT_NODE) return;
    el.style.setProperty('user-select', 'none', 'important');
    el.style.setProperty('-webkit-user-select', 'none', 'important');
    el.style.setProperty('-moz-user-select', 'none', 'important');
    el.style.setProperty('-ms-user-select', 'none', 'important');
}

export default function NoSelectHtml({ html, id, className, style }) {
    const rootRef = useRef(null);

    // Capa 2: sobreescribe inline styles del CMS tras cada render
    useLayoutEffect(() => {
        const root = rootRef.current;
        if (!root) return;
        applyNoSelectToNode(root);
        root.querySelectorAll('*').forEach(applyNoSelectToNode);
    }, [html]);

    // Capa 3: listeners en document
    useEffect(() => {
        const root = rootRef.current;
        if (!root) return;

        const selectionTouchesRoot = () => {
            const sel = document.getSelection();
            if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return false;
            for (let i = 0; i < sel.rangeCount; i++) {
                if (sel.getRangeAt(i).intersectsNode(root)) return true;
            }
            return false;
        };

        const onSelectStart = (e) => {
            if (root.contains(e.target)) e.preventDefault();
        };

        const onSelectionChange = () => {
            if (selectionTouchesRoot()) document.getSelection().removeAllRanges();
        };

        const onCopyOrCut = (e) => {
            if (selectionTouchesRoot()) {
                e.preventDefault();
                e.stopPropagation();
            }
        };

        document.addEventListener('selectstart', onSelectStart, true);
        document.addEventListener('selectionchange', onSelectionChange);
        document.addEventListener('copy', onCopyOrCut, true);
        document.addEventListener('cut', onCopyOrCut, true);

        return () => {
            document.removeEventListener('selectstart', onSelectStart, true);
            document.removeEventListener('selectionchange', onSelectionChange);
            document.removeEventListener('copy', onCopyOrCut, true);
            document.removeEventListener('cut', onCopyOrCut, true);
        };
    }, [html]);

    return (
        <div
            ref={rootRef}
            id={id}
            className={className}
            style={{ ...noSelectInlineStyle, ...style }}
            dangerouslySetInnerHTML={{ __html: html ?? '' }}
        />
    );
}

/**
 * Misma protección que NoSelectHtml pero para hijos React (sin dangerouslySetInnerHTML).
 * Usa MutationObserver para volver a aplicar estilos cuando el subárbol cambia (p. ej. más términos).
 * El contenedor debe ser el propio scroll (p. ej. #container_term) para que contains() y captura de eventos cubran toda la lista.
 */
export function NoSelectRegion({ children, className, style, id, ...rest }) {
    const rootRef = useRef(null);

    useLayoutEffect(() => {
        const root = rootRef.current;
        if (!root) return;
        const apply = () => {
            applyNoSelectToNode(root);
            root.querySelectorAll('*').forEach(applyNoSelectToNode);
        };
        apply();
        const obs = new MutationObserver(apply);
        obs.observe(root, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'class'],
        });
        return () => obs.disconnect();
    }, []);

    useEffect(() => {
        const root = rootRef.current;
        if (!root) return;

        const selectionTouchesRoot = () => {
            const sel = document.getSelection();
            if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return false;
            for (let i = 0; i < sel.rangeCount; i++) {
                if (sel.getRangeAt(i).intersectsNode(root)) return true;
            }
            return false;
        };

        const onSelectStart = (e) => {
            if (root.contains(e.target)) e.preventDefault();
        };

        const onSelectionChange = () => {
            if (selectionTouchesRoot()) document.getSelection().removeAllRanges();
        };

        const onCopyOrCut = (e) => {
            if (selectionTouchesRoot()) {
                e.preventDefault();
                e.stopPropagation();
            }
        };

        const onKeyDown = (e) => {
            if (!(e.ctrlKey || e.metaKey) || (e.key !== 'a' && e.key !== 'A')) return;
            const active = document.activeElement;
            if (active && root.contains(active)) {
                e.preventDefault();
                e.stopPropagation();
            }
        };

        // Raíz + document: WebKit a veces solo dispara selectstart en el nodo concreto
        root.addEventListener('selectstart', onSelectStart, true);
        document.addEventListener('selectstart', onSelectStart, true);
        document.addEventListener('selectionchange', onSelectionChange);
        document.addEventListener('copy', onCopyOrCut, true);
        document.addEventListener('cut', onCopyOrCut, true);
        document.addEventListener('keydown', onKeyDown, true);

        return () => {
            root.removeEventListener('selectstart', onSelectStart, true);
            document.removeEventListener('selectstart', onSelectStart, true);
            document.removeEventListener('selectionchange', onSelectionChange);
            document.removeEventListener('copy', onCopyOrCut, true);
            document.removeEventListener('cut', onCopyOrCut, true);
            document.removeEventListener('keydown', onKeyDown, true);
        };
    }, []);

    const blockClipboard = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    return (
        <div
            ref={rootRef}
            id={id}
            className={className}
            style={{ ...noSelectInlineStyle, ...style }}
            {...rest}
            onCopyCapture={blockClipboard}
            onCutCapture={blockClipboard}
        >
            {children}
        </div>
    );
}
